import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Bolt, CheckCircle, LinkOff, WarningAmber } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ConfirmModal from "../components/modals/ConfirmModal";
import SavingOverlay from "../components/SavingOverlay";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, pageHeader } from "../theme/designSystem";
import {
  signatureErrorMessage,
  signatureSettingsService,
  SignatureConnectionStatus,
  SignatureEnvironment,
  SignatureProviderOption,
} from "../services/signatureService";

/**
 * Tecnologia > Assinatura eletrônica (item 67, RN-558c). A unidade conecta a PRÓPRIA conta no provedor
 * (a plataforma não tem conta nem vê os contratos dela). A chave só vai para o servidor, cifrada;
 * a tela nunca a mostra de volta. Trocar a chave = conectar de novo.
 */

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "";

const SignatureSettings: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();

  const [providers, setProviders] = useState<SignatureProviderOption[]>([]);
  const [status, setStatus] = useState<SignatureConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"" | "connect" | "test" | "disconnect">("");
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [replacing, setReplacing] = useState(false);

  const [providerCode, setProviderCode] = useState("");
  const [environment, setEnvironment] = useState<SignatureEnvironment>("production");
  const [apiKey, setApiKey] = useState("");
  const [accountId, setAccountId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, current] = await Promise.all([signatureSettingsService.getProviders(), signatureSettingsService.getStatus()]);
      setProviders(list);
      setStatus(current);
      setProviderCode(current.connection?.providerCode || list[0]?.code || "");
      setEnvironment(current.connection?.environment || "production");
    } catch (e) {
      showToast(signatureErrorMessage(e, "Erro ao carregar a configuração de assinatura."), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const connection = status?.connection ?? null;
  const showForm = !connection || replacing;

  const connect = async () => {
    if (!providerCode) { showToast("Escolha o provedor.", "error"); return; }
    if (!apiKey.trim()) { showToast("Informe a chave de API do provedor.", "error"); return; }
    setBusy("connect");
    try {
      await signatureSettingsService.connect({ providerCode, apiKey: apiKey.trim(), accountId: accountId.trim() || null, environment });
      setApiKey("");
      setAccountId("");
      setReplacing(false);
      showToast("Provedor de assinatura conectado.", "success");
      await load();
    } catch (e) {
      showToast(signatureErrorMessage(e, "Não foi possível conectar. Confira a chave e o ambiente."), "error");
    } finally {
      setBusy("");
    }
  };

  const test = async () => {
    setBusy("test");
    try {
      await signatureSettingsService.test();
      showToast("Conexão com o provedor OK.", "success");
      await load();
    } catch (e) {
      showToast(signatureErrorMessage(e, "A conexão com o provedor falhou."), "error");
    } finally {
      setBusy("");
    }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try {
      await signatureSettingsService.disconnect();
      setConfirmDisconnect(false);
      showToast("Provedor desconectado.", "success");
      await load();
    } catch (e) {
      showToast(signatureErrorMessage(e, "Não foi possível desconectar."), "error");
    } finally {
      setBusy("");
    }
  };

  const labelSx = { fontSize: "0.78rem", color: colors.textSecondary };
  const valueSx = { fontSize: "0.92rem", color: colors.textPrimary, fontWeight: 500, wordBreak: "break-word" as const };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Assinatura eletrônica</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Conecte a conta da clínica no provedor de assinatura para enviar contratos aos pacientes. Cada unidade usa a própria conta.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel" style={{ position: "relative" }}>
            <SavingOverlay open={busy === "connect"} message="Conferindo a chave no provedor..." />
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: colors.primary }} /></Box>
            ) : !status?.serverConfigured ? (
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", py: 2 }}>
                <WarningAmber sx={{ color: colors.warning }} />
                <Typography sx={{ color: colors.textPrimary }}>
                  A assinatura eletrônica ainda não está liberada neste servidor. Avise o suporte do Clinic4Us.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 760 }}>
                {connection && (
                  <Box sx={{ border: `1px solid ${colors.backgroundAlt}`, borderRadius: "10px", p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
                      <CheckCircle sx={{ color: colors.success }} />
                      <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>Conectado a {connection.providerName}</Typography>
                      <Chip
                        size="small"
                        label={connection.environment === "sandbox" ? "Ambiente de teste" : "Produção"}
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          backgroundColor: connection.environment === "sandbox" ? colors.warningSoft : colors.successSoft,
                          color: connection.environment === "sandbox" ? colors.warningSoftText : colors.successHover,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                      <Box>
                        <Typography sx={labelSx}>Conta no provedor</Typography>
                        <Typography sx={valueSx}>{connection.accountName || connection.accountId || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={labelSx}>Conectado por</Typography>
                        <Typography sx={valueSx}>{connection.connectedByName || "—"} · {formatDateTime(connection.connectedAt)}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={labelSx}>Última conferência</Typography>
                        <Typography sx={valueSx}>{formatDateTime(connection.lastCheckedAt) || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={labelSx}>Aviso automático de assinatura</Typography>
                        <Typography sx={valueSx}>
                          {connection.webhookRegistered ? "Ativo" : "Inativo: a situação atualiza ao abrir a aba Contratos do paciente"}
                        </Typography>
                      </Box>
                    </Box>
                    {connection.environment === "sandbox" && (
                      <Typography sx={{ fontSize: "0.8rem", color: colors.warningSoftText, mt: 2 }}>
                        Ambiente de teste: as assinaturas não têm validade jurídica. Para uso real, conecte de novo com a chave de produção.
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2 }}>
                      <Button variant="outlined" onClick={test} disabled={busy !== ""} startIcon={busy === "test" ? <CircularProgress size={16} /> : <Bolt />} sx={{ textTransform: "none", borderColor: colors.primary, color: colors.primary }}>
                        Testar conexão
                      </Button>
                      {!replacing && (
                        <Button variant="outlined" onClick={() => setReplacing(true)} disabled={busy !== ""} sx={{ textTransform: "none", borderColor: colors.border, color: colors.textSecondary }}>
                          Trocar chave ou provedor
                        </Button>
                      )}
                      <Button variant="outlined" onClick={() => setConfirmDisconnect(true)} disabled={busy !== ""} startIcon={<LinkOff />} sx={{ textTransform: "none", borderColor: colors.error, color: colors.error }}>
                        Desconectar
                      </Button>
                    </Box>
                  </Box>
                )}

                {showForm && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>
                      {connection ? "Conectar de novo" : "Conectar provedor"}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel shrink>Provedor</InputLabel>
                        <Select label="Provedor" value={providerCode} onChange={(e) => setProviderCode(e.target.value)} notched>
                          {providers.map((p) => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth>
                        <InputLabel shrink>Ambiente</InputLabel>
                        <Select label="Ambiente" value={environment} onChange={(e) => setEnvironment(e.target.value as SignatureEnvironment)} notched>
                          <MenuItem value="production">Produção</MenuItem>
                          <MenuItem value="sandbox">Teste (sem validade jurídica)</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="password"
                        label="Chave de API"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        autoComplete="new-password"
                        inputProps={{ maxLength: 500 }}
                        InputLabelProps={{ shrink: true }}
                        helperText="No painel do provedor, na área de API. A chave fica cifrada e não aparece de novo."
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="Id da conta (opcional)"
                        value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                        inputProps={{ maxLength: 100 }}
                        InputLabelProps={{ shrink: true }}
                        helperText="Só se a chave tiver acesso a mais de uma conta."
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                      <Button variant="contained" onClick={connect} disabled={busy !== ""} sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}>
                        Conectar
                      </Button>
                      {replacing && (
                        <Button variant="outlined" onClick={() => { setReplacing(false); setApiKey(""); setAccountId(""); }} disabled={busy !== ""} sx={{ textTransform: "none", borderColor: colors.border, color: colors.textSecondary }}>
                          Cancelar
                        </Button>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
                      Ao conectar, o sistema confere a chave no provedor e liga o aviso automático de assinatura. Os contratos assinados
                      são baixados para o armazenamento da clínica assim que ficam prontos.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </div>
        </Container>
      </Box>

      <ConfirmModal
        isOpen={confirmDisconnect}
        title="Desconectar o provedor"
        message="Novos contratos não poderão ser enviados para assinatura até conectar de novo."
        warningMessage="Contratos já enviados continuam no provedor, mas a situação deles não atualiza mais por aqui. Os já assinados seguem guardados."
        confirmButtonText="Desconectar"
        onConfirm={disconnect}
        onCancel={() => setConfirmDisconnect(false)}
        type="danger"
        isProcessing={busy === "disconnect"}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default SignatureSettings;
